import { useState } from "react";
import { TrendingUp, TrendingDown, Calendar, Clock, Zap, Award, Target, Info } from "lucide-react";

const InsightsDashboard = ({ 
  stats = {
    total_sessions: 142,
    trampoline: {
      avg_tof: { week: 0, month: 1.19, week_change: 4.2, month_change: 6.7 },
      avg_stopwatch: { week: 32.5, month: 31.8, week_change: 2.2, month_change: 3.1 },
      weekly_stats: { 
        hours: 0, 
        difficulty: 245, 
        skills: 87, 
        hours_change: 0, 
        difficulty_change: -2.3, 
        skills_change: 15.2,
        skill_density: 12.5,
        skill_density_change: 8.3,
        difficulty_density: 35.2,
        difficulty_density_change: -3.1,
        avg_difficulty_per_skill: 2.82,
        avg_difficulty_per_skill_change: 5.6,
        difficulty_intensity: 4.1,
        difficulty_intensity_change: 2.8,
        skill_intensity: 1.45,
        skill_intensity_change: -1.2
      }
    },
    dmt: {
      weekly_stats: { 
        hours: 8.2, 
        difficulty: 180, 
        skills: 52, 
        hours_change: 5.1, 
        difficulty_change: 3.4, 
        skills_change: 12.5,
        skill_density: 10.3,
        skill_density_change: 6.2,
        difficulty_density: 28.7,
        difficulty_density_change: 4.5,
        avg_difficulty_per_skill: 3.46,
        avg_difficulty_per_skill_change: -2.1,
        difficulty_intensity: 3.8,
        difficulty_intensity_change: 1.5,
        skill_intensity: 1.09,
        skill_intensity_change: 3.7
      }
    },
    tumbling: {
      weekly_stats: { 
        hours: 0, 
        difficulty: 195, 
        skills: 64, 
        hours_change: -1.2, 
        difficulty_change: 8.9, 
        skills_change: 7.3,
        skill_density: 11.8,
        skill_density_change: 9.1,
        difficulty_density: 31.5,
        difficulty_density_change: 7.8,
        avg_difficulty_per_skill: 3.05,
        avg_difficulty_per_skill_change: 1.6,
        difficulty_intensity: 3.9,
        difficulty_intensity_change: 10.1,
        skill_intensity: 1.28,
        skill_intensity_change: 8.4
      }
    }
  },
  onNavigate,
  currentDiscipline = 'trampoline'
}) => {
  const [discipline, setDiscipline] = useState(currentDiscipline);
  const [activeInfo, setActiveInfo] = useState(null);

  const metricDescriptions = {
    difficulty_per_skill: "Average difficulty per skill performed",
    skills_density: "Average number of skills per effort",
    difficulty_density: "Average difficulty per effort",
    difficulty_intensity: "Average difficulty per hour of training",
    skills_intensity: "Average number of skills per hour of training",
    tof: "Average time of flight per skill from recorded efforts in the last week/month",
    stopwatch: "Average stopwatch time per skill from recorded efforts in the last week/month"
  };

  const StatCard = ({ icon: Icon, label, value, change, unit = "", isTime = false, isHours = false, isDecimal = false, infoKey }) => {
    const isPositive = change >= 0;
    const TrendIcon = isPositive ? TrendingUp : TrendingDown;
    
    // Format value based on type
    let formattedValue = value;
    let displayUnit = unit;
    
    if (isTime) {
      formattedValue = Number(value).toFixed(3);
    } else if (isHours) {
      if (value >= 1) {
        formattedValue = Number(value).toFixed(1);
        displayUnit = 'h';
      } else {
        formattedValue = Math.round(value * 60);
        displayUnit = 'min';
      }
    } else if (isDecimal) {
      formattedValue = Number(value).toFixed(1);
    }
    
    // Format change to 1 decimal place
    const formattedChange = Math.abs(change).toFixed(1);
    
    return (
      <div className="bg-white rounded-xl p-4 border-2 border-gray-100 hover:border-blue-300 transition-all relative">
        {infoKey && (
          <button
            onClick={() => setActiveInfo(activeInfo === infoKey ? null : infoKey)}
            className="absolute top-2 right-2 text-gray-400 hover:text-blue-600 transition-colors"
          >
            <Info size={16} />
          </button>
        )}
        {activeInfo === infoKey && (
          <div className="absolute top-10 right-2 bg-gray-900 text-white text-sm rounded-lg p-3 shadow-lg z-10 max-w-xs">
            {metricDescriptions[infoKey]}
          </div>
        )}
        <div className="flex items-center gap-2 mb-2">
          <Icon size={18} className="text-blue-600" />
          <span className="text-base font-medium text-gray-600">{label}</span>
        </div>
        <div className="flex items-end justify-between">
          <div className="text-2xl font-bold text-gray-900">
            {formattedValue}{displayUnit}
          </div>
          <div className={`flex items-center gap-1 text-base font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            <TrendIcon size={16} />
            {formattedChange}%
          </div>
        </div>
      </div>
    );
  };

  const CategoryButton = ({ emoji, label, type }) => (
    <button
      onClick={() => onNavigate?.({
        discipline: discipline,
        objectiveType: type,
        objective: ''
    })}
      className="flex items-center gap-3 w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
    >
      <span className="text-2xl">{emoji}</span>
      <span className="text-xl font-semibold text-gray-700 group-hover:text-blue-700">{label}</span>
    </button>
  );

  const currentStats = stats && stats[discipline];
  const showTOFAndStopwatch = discipline === 'trampoline';

  return (
    <div className="pt-6 mb-56 new-entry">
        <div className="max-w-4xl mx-auto space-y-6 overflow-y-scroll">

            {/* Streak Card */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex items-center justify-between">
                <div>
                <div className="flex items-center gap-2 mb-2">
                    <Calendar size={24} />
                    <span className="text-lg font-medium opacity-90">Total Sessions Logged</span>
                </div>
                <div className="text-5xl font-bold">{stats?.total_sessions}</div>
                <p className="text-lg mt-2">Keep up the great work! 🔥</p>
                </div>
                <div className="text-6xl opacity-40">🤸</div>
            </div>
            </div>

            {/* Discipline Selector */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-semibold text-gray-600">Discipline:</span>
                {['trampoline', 'dmt', 'tumbling'].map(disc => (
                <button
                    key={disc}
                    onClick={() => setDiscipline(disc)}
                    className={`px-4 py-2 rounded-lg font-semiboldtransition-all ${
                    discipline === disc
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={{ fontSize: '1.2rem' }}
                >
                    {disc.charAt(0).toUpperCase() + disc.slice(1)}
                </button>
                ))}
            </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Performance Metrics</h2>
            
            <div className="space-y-4">
                {/* TOF and Stopwatch - Only for Trampoline */}
                {showTOFAndStopwatch && (
                <>
                    {(currentStats?.avg_tof.week > 0 || currentStats?.avg_tof.month > 0) && 
                      <div className="pb-4 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-600 mb-3">Time of Flight</h3>
                      <div className="grid grid-cols-2 gap-3">
                          <StatCard 
                          icon={Zap} 
                          label="Week Avg" 
                          value={currentStats?.avg_tof.week} 
                          change={currentStats?.avg_tof.week_change}
                          unit="s"
                          isTime={true}
                          infoKey={"tof"}
                          />
                          <StatCard 
                          icon={Zap} 
                          label="Month Avg" 
                          value={currentStats?.avg_tof.month} 
                          change={currentStats?.avg_tof.month_change}
                          unit="s"
                          isTime={true}
                          />
                      </div>
                    </div>}

                    {(currentStats?.avg_stopwatch.week > 0 || currentStats?.avg_stopwatch.month > 0) && 
                      <div className="pb-4 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-600 mb-3">Stopwatch Time</h3>
                      <div className="grid grid-cols-2 gap-3">
                          <StatCard 
                          icon={Clock} 
                          label="Week Avg" 
                          value={currentStats?.avg_stopwatch.week} 
                          change={currentStats?.avg_stopwatch.week_change}
                          unit="s"
                          isTime={true}
                          infoKey={"stopwatch"}
                          />
                          <StatCard 
                          icon={Clock} 
                          label="Month Avg" 
                          value={currentStats?.avg_stopwatch.month} 
                          change={currentStats?.avg_stopwatch.month_change}
                          unit="s"
                          isTime={true}
                          />
                      </div>
                    </div>}
                </>
                )}

                {/* Weekly Summary - Always visible */}
                <div>
                <h3 className="text-lg font-semibold text-gray-600 mb-3">Weekly Load</h3>
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <StatCard 
                    icon={Clock} 
                    label="Hours" 
                    value={currentStats?.weekly_stats.hours} 
                    change={currentStats?.weekly_stats.hours_change}
                    isHours={true}
                    />
                    <StatCard 
                    icon={Target} 
                    label="Skills" 
                    value={currentStats?.weekly_stats.skills} 
                    change={currentStats?.weekly_stats.skills_change}
                    />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                    <StatCard 
                    icon={Award} 
                    label="Difficulty" 
                    value={currentStats?.weekly_stats.difficulty} 
                    change={currentStats?.weekly_stats.difficulty_change}
                    />
                    <StatCard 
                    icon={Award} 
                    label="Avg DD Per Skill" 
                    value={currentStats?.weekly_stats.difficulty_per_skill} 
                    change={currentStats?.weekly_stats.difficulty_per_skill_change}
                    isDecimal={true}
                    />
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <StatCard 
                    icon={Award} 
                    label="Difficulty Density" 
                    value={currentStats?.weekly_stats.difficulty_density} 
                    change={currentStats?.weekly_stats.difficulty_density_change}
                    isDecimal={true}
                    infoKey={"difficulty_density"}
                    />
                    <StatCard 
                    icon={Target} 
                    label="Skill Density" 
                    value={currentStats?.weekly_stats.skills_density} 
                    change={currentStats?.weekly_stats.skills_density_change}
                    isDecimal={true}
                    infoKey={"skills_density"}
                    />
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <StatCard 
                    icon={Zap} 
                    label="Diff Intensity" 
                    value={currentStats?.weekly_stats.difficulty_intensity} 
                    change={currentStats?.weekly_stats.difficulty_intensity_change}
                    unit="/h"
                    isDecimal={true}
                    infoKey={"difficulty_intensity"}
                    />
                    <StatCard 
                    icon={Zap} 
                    label="Skill Intensity" 
                    value={currentStats?.weekly_stats.skills_intensity} 
                    change={currentStats?.weekly_stats.skills_intensity_change}
                    unit="/h"
                    isDecimal={true}
                    infoKey={"skills_intensity"}
                    />
                </div>
                </div>
            </div>
            </div>

            {/* Category Navigation */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Explore Data</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CategoryButton emoji="🤸" label="Routines" type="routines" />
                {discipline !== 'dmt' && <CategoryButton emoji="🎯" label="Drills" type="drills" />}
                <CategoryButton emoji="🏆" label="Results" type="results" />
                <CategoryButton emoji="📅" label="Sessions" type="sessions" />
                <CategoryButton emoji="💪" label="Conditioning Tests" type="conditioning" />
            </div>
            </div>
        </div>
    </div>
  );
};

export default InsightsDashboard;